import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { IconButton } from '../ui/Button';
import { X } from '../ui/icons';

export type DockTab = 'changes' | 'logs';

interface DockState {
  open: boolean;
  width: number;
  tab: DockTab;
}

const KEY = 'hue.dock';

function load(): DockState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { open: false, width: 360, tab: 'changes', ...(JSON.parse(raw) as Partial<DockState>) };
  } catch {
    // ignore
  }
  return { open: false, width: 360, tab: 'changes' };
}

export function useDock() {
  const [state, setState] = useState<DockState>(() => load());
  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);
  const show = useCallback((tab?: DockTab) => setState((s) => ({ ...s, open: true, tab: tab ?? s.tab })), []);
  const hide = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const toggle = useCallback((tab?: DockTab) => setState((s) => (s.open && (!tab || s.tab === tab) ? { ...s, open: false } : { ...s, open: true, tab: tab ?? s.tab })), []);
  const setTab = useCallback((tab: DockTab) => setState((s) => ({ ...s, tab })), []);
  const setWidth = useCallback((width: number) => setState((s) => ({ ...s, width: Math.min(800, Math.max(240, width)) })), []);
  return { ...state, show, hide, toggle, setTab, setWidth };
}

/** The right dock (spec section 3.3): tabs Changes / Logs, resizable, its width remembered. */
export function Dock({ dock, changes, logs }: { dock: ReturnType<typeof useDock>; changes: ReactNode; logs: ReactNode }) {
  const dragging = useRef(false);
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (dragging.current) dock.setWidth(window.innerWidth - e.clientX);
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dock]);
  if (!dock.open) return null;
  return (
    <div className="dock-wrap">
      <div className="dock-resize" role="separator" aria-orientation="vertical" aria-label="Resize panel" onMouseDown={() => (dragging.current = true)} />
      <aside className="dock" style={{ ['--hue-dock-w' as string]: `${dock.width}px` }} aria-label="Side panel">
        <div className="dock-tabs" role="tablist">
          <button type="button" role="tab" className={`tab ${dock.tab === 'changes' ? 'active' : ''}`} aria-selected={dock.tab === 'changes'} onClick={() => dock.setTab('changes')}>
            Changes
          </button>
          <button type="button" role="tab" className={`tab ${dock.tab === 'logs' ? 'active' : ''}`} aria-selected={dock.tab === 'logs'} onClick={() => dock.setTab('logs')}>
            Logs
          </button>
          <span className="spacer" />
          <IconButton label="Close panel" onClick={dock.hide}>
            <X />
          </IconButton>
        </div>
        <div className="dock-body" role="tabpanel">
          {dock.tab === 'changes' ? changes : logs}
        </div>
      </aside>
    </div>
  );
}
